import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import blogApi, { blogImageUrl } from "../../apiProvider/blogApi";
import { useTranslation } from "../../context/TranslationContext";
import "./blog.css";

const BlogPage = () => {
  const { translateSync } = useTranslation();
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const pageSize = 24;

  useEffect(() => {
    setLoading(true);
    blogApi.list({ page, limit: pageSize }).then((data) => {
      setBlogs(data?.data || []);
      setTotal(Number(data?.total) || 0);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [page]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const changePage = (nextPage) => {
    setPage(nextPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const schema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Colours Boutique Blog",
    "url": "https://coloursboutique.com/blog",
    "description": "Silk saree guides, styling ideas, care tips, and Singapore saree shopping advice from Colours Boutique."
  };

  return (
    <>
      <Helmet>
        <title>Silk Saree Blog | Colours Boutique Singapore</title>
        <meta name="description" content="Read Colours Boutique silk saree guides, Kanchipuram saree tips, saree care ideas, and styling advice for Singapore and India shoppers." />
        <link rel="canonical" href="https://coloursboutique.com/blog" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <div className="blog-page-shell">
        <section className="blog-hero">
          <div className="container">
            <p className="blog-eyebrow">Colours Boutique Journal</p>
            <h1 className="blog-hero-title">{translateSync("Silk Saree Blog")}</h1>
            <nav aria-label="breadcrumb" className="boutique-breadcrumb-nav center-align">
              <ol className="boutique-breadcrumb-ol boutique-breadcrumb-center">
                <li className="breadcrumb-item"><span onClick={() => navigate("/")}>{translateSync("Home")}</span></li>
                <li className="breadcrumb-item active">{translateSync("Blog")}</li>
              </ol>
            </nav>
          </div>
        </section>

        <section className="blog-index-section">
          <div className="container">
            {loading ? (
              <p>{translateSync("Loading blog posts...")}</p>
            ) : (
              <div className="row g-4">
                {blogs.length === 0 && <p>{translateSync("No blog posts published yet.")}</p>}
                {blogs.map((blog) => (
                  <div className="col-md-6 col-lg-4" key={blog._id}>
                    <article className="blog-index-card" onClick={() => navigate(`/blog/${blog.slug}`)}>
                      <img src={blogImageUrl(blog.coverImage) || "/assets/images/logo.png"} alt={blog.title} />
                      <div className="blog-index-card-body">
                        <div className="blog-card-meta">{blog.category?.name || "Saree Guide"} {blog.publishedAt ? `• ${new Date(blog.publishedAt).toLocaleDateString()}` : ""}</div>
                        <h2>{translateSync(blog.title)}</h2>
                        <p>{translateSync(blog.excerpt || "")}</p>
                        <span className="blog-read-link">{translateSync("Read Guide")}</span>
                      </div>
                    </article>
                  </div>
                ))}
              </div>
            )}

            {!loading && totalPages > 1 && (
              <nav className="blog-pagination" aria-label="Blog pagination">
                <button
                  type="button"
                  className="blog-pagination-button"
                  disabled={page === 0}
                  onClick={() => changePage(page - 1)}
                >
                  Previous
                </button>
                <span className="blog-pagination-status">
                  Page {page + 1} of {totalPages}
                </span>
                <button
                  type="button"
                  className="blog-pagination-button"
                  disabled={page >= totalPages - 1}
                  onClick={() => changePage(page + 1)}
                >
                  Next
                </button>
              </nav>
            )}
          </div>
        </section>
      </div>
    </>
  );
};

export default BlogPage;
